import React from 'react';

import { Skeleton } from '../skeleton';
import { TableCell, TableRow } from './Table';
import type { ITableSectionColumn } from './types';
import {
	ACTIONS_COLUMN_ID,
	CHECKBOX_COLUMN_WIDTH_PX,
	DEFAULT_SKELETON_ROW_COUNT,
} from './types';

/**
 * Placeholder widths in characters, cycled by column index so a loading
 * table reads like text of differing lengths rather than a grid of
 * identical bars. Cycled by column rather than by row, and fixed rather
 * than random, so every cell of a column is the same width and the column
 * does not change size between frames.
 *
 * Characters, not percentages: a percentage width contributes nothing to
 * the intrinsic width of a column, so percentage placeholders left the
 * columns sized by their headers and the table re-laid itself out the
 * moment data arrived. A column can override this with `skeletonWidthCh`.
 */
const SKELETON_WIDTH_CH_CYCLE = [12, 9, 14, 7] as const;

/**
 * Placeholder heights, matched to what the loaded row puts in the same
 * cell: body text has a 20px line box and an action button is 24px tall.
 * A shorter bar made every row ~8px shorter while loading, so the table
 * grew as the data landed.
 */
const TEXT_SKELETON_HEIGHT_CLASS = 'h-20';
const ACTIONS_SKELETON_HEIGHT_CLASS = 'h-24';

/** Placeholders for the fixed-width checkbox and actions columns. */
const CHECKBOX_SKELETON_CLASS = 'size-16 rounded-xxs';
const ACTIONS_SKELETON_WIDTH_CH = 14;

/** The placeholder width for a column, in characters. */
function getSkeletonWidthCh(
	column: ITableSectionColumn,
	columnIndex: number
): number {
	if (column.skeletonWidthCh !== undefined) return column.skeletonWidthCh;
	if (column.id === ACTIONS_COLUMN_ID) return ACTIONS_SKELETON_WIDTH_CH;
	return SKELETON_WIDTH_CH_CYCLE[columnIndex % SKELETON_WIDTH_CH_CYCLE.length];
}

export interface ITableSkeletonRowsProps {
	/** The same columns the loaded table renders, so the shape matches. */
	columns: ITableSectionColumn[];
	/** Rows to draw. Pass the page size to keep the height identical. */
	rowCount?: number;
	/** True when the table renders a leading checkbox column. */
	hasCheckboxColumn?: boolean;
}

/**
 * The rows shown in place of data while a table loads.
 *
 * Structure comes from the same `columns` array as the loaded table —
 * count, alignment and sticky behaviour included — so the two render at
 * the same width and the table does not jump when data lands.
 *
 * Placeholders are sized like the content they stand in for: the height
 * of a line of body text (or of an action button), and a width in
 * characters the column can tune with `skeletonWidthCh`. Both matter,
 * because the table sizes itself from its cells — a placeholder shorter
 * or narrower than the data makes the table resize the moment the data
 * arrives.
 *
 * Every cell is `aria-hidden`: the placeholders carry no information, and
 * a screen reader announcing forty empty cells is worse than silence. The
 * body that hosts these rows owns `aria-busy` and the single status
 * announcement.
 */
function TableSkeletonRows({
	columns,
	rowCount = DEFAULT_SKELETON_ROW_COUNT,
	hasCheckboxColumn = false,
}: Readonly<ITableSkeletonRowsProps>) {
	return (
		<>
			{Array.from({ length: rowCount }, (_unused, rowIndex) => (
				<TableRow key={rowIndex} aria-hidden isHoverDisabled>
					{hasCheckboxColumn && (
						<TableCell align="center" className="w-12 pr-0" sticky="left">
							<Skeleton className={CHECKBOX_SKELETON_CLASS} />
						</TableCell>
					)}
					{columns.map((column, columnIndex) => {
						const isActionsColumn = column.id === ACTIONS_COLUMN_ID;
						return (
							<TableCell
								key={column.id}
								align={column.align ?? (isActionsColumn ? 'right' : 'left')}
								sticky={column.sticky}
								stickyLeftOffset={
									column.sticky === 'left' && hasCheckboxColumn
										? CHECKBOX_COLUMN_WIDTH_PX
										: undefined
								}
							>
								<Skeleton
									className={
										isActionsColumn
											? `ml-auto ${ACTIONS_SKELETON_HEIGHT_CLASS}`
											: TEXT_SKELETON_HEIGHT_CLASS
									}
									style={{
										width: `${getSkeletonWidthCh(column, columnIndex)}ch`,
									}}
								/>
							</TableCell>
						);
					})}
				</TableRow>
			))}
		</>
	);
}

export { TableSkeletonRows };
